import { useState } from 'react';
import type { RosterPlayerInput } from '../../app/roster';
import { config } from '../../config';
import { ourPlayerLabel } from '../../domain/teamProfile';
import { ATTRIBUTES } from '../../types';
import type {
  AttributeGroup,
  AttributeKey,
  AttributeRating,
  OurPlayerRow,
  Position,
} from '../../types';
import { SheetB } from '../SheetB';
import { PositionGrid } from './PositionGrid';
import { RatingControl } from './RatingControl';

/**
 * §17.2 — the My Team **player sheet**: number, first name, two position grids, nineteen ratings
 * in four collapsible groups, the availability switch and the armed remove (build unit B-28).
 *
 * ── Ruling R-1 governs the name ─────────────────────────────────────────────
 *
 * The hint copy used to read *"no names needed"*, carrying design §10's *"no names, no PII"* into
 * a surface about the user's OWN squad. Andrew, 2026-08-18: *"First names are used throughout
 * youth sports… There should be no restriction on the use of someone's first name. That's not PII
 * in any true meaning of PII… **Change the rule, don't change my spec for the player data or what
 * gets transmitted.**"* ENV-11's no-identity rule governs **opponent** players, whom the user is
 * scouting; his own squad is a different population.
 *
 * ── The most load-bearing sentence on the screen is under the second grid ───
 *
 * *"Coach Claude will never suggest moving this player."* / *"Coach Claude may move this player to
 * RM or CM, and nowhere else."* §3.7.36's execution filter, **stated where the decision is made**,
 * and mirroring the prose the model will actually receive. It is styled as a **statement, not a
 * warning**: an empty list is a deliberate, meaningful final state, and styling it as an incomplete
 * form would push users to fill it in to make the nag go away — which is precisely how a
 * garbage-time cameo gets recorded as permission to move a player in a game that is in doubt.
 *
 * ── Typing is legal here ────────────────────────────────────────────────────
 *
 * The My Team tab lives in the out-of-match shell — no in-play state can render it — so the
 * no-required-typing-in-play rule has nothing to say. Every input is ≥16 px computed (the iOS
 * force-zoom rule); the primary action sits in the sheet's sticky top header where the keyboard
 * cannot cover it (SheetB's structural rule). The number field is `type="text"` +
 * `inputmode="numeric"` — a numeric keypad **without** `type="number"`'s stepper, which is a
 * 20 pt target beside a 44 pt one.
 *
 * ── Nineteen attributes is ~5 screens of scroll, and that is affordable ─────
 *
 * Accepted residual (b): entry burden is explicitly not a constraint (§3.7.37), and **four
 * collapsed headers is the default view** — the sheet is optimised for review and correction
 * (*"is this profile still true?"*), not for a first-run wizard, of which §17 says there is none.
 */

/** §3.7.37's four groups, in the requirement's order and its own words. Exhaustive `Record` — a
 *  fifth group without a heading is a compile error rather than a nameless section. */
const GROUP_TITLE: Readonly<Record<AttributeGroup, string>> = {
  onTheBall: 'On the ball',
  gettingForward: 'Getting forward',
  defending: 'Defending',
  headAndVoice: 'Head and voice',
};

const GROUP_ORDER: readonly AttributeGroup[] = [
  'onTheBall',
  'gettingForward',
  'defending',
  'headAndVoice',
];

type Ratings = Partial<Record<AttributeKey, AttributeRating>>;

export interface PlayerSheetProps {
  /** `null` = adding a new player; a row = editing that player. */
  readonly player: OurPlayerRow | null;
  /** The rest of the squad — for §17.2's non-blocking duplicate-number line. */
  readonly squad: readonly OurPlayerRow[];
  readonly busy: boolean;
  /** A rejection sentence from the engine (`DispatchResult.message`), rendered in the sheet. */
  readonly problem: string | null;
  readonly onSave: (input: RosterPlayerInput) => void;
  readonly onDelete: (playerId: string) => void;
  readonly onClose: () => void;
}

export function PlayerSheet({
  player,
  squad,
  busy,
  problem,
  onSave,
  onDelete,
  onClose,
}: PlayerSheetProps) {
  const [jersey, setJersey] = useState(
    player === null ? '' : String(player.jerseyNumber),
  );
  const [firstName, setFirstName] = useState(player?.firstName ?? '');
  const [primary, setPrimary] = useState<Position | null>(player?.primaryPosition ?? null);
  const [secondaries, setSecondaries] = useState<readonly Position[]>(
    player?.secondaryPositions ?? [],
  );
  const [ratings, setRatings] = useState<Ratings>({ ...(player?.ratings ?? {}) });
  const [armedRemove, setArmedRemove] = useState(false);
  /**
   * §17.2 — *"the first group open when adding, a group with ratings open when editing."* Computed
   * once from the row this sheet opened on, never re-derived, so a group does not shut itself while
   * the user is clearing its last rating.
   */
  const [open, setOpen] = useState<ReadonlySet<AttributeGroup>>(() => initialOpenGroups(player));

  const jerseyNumber = Number.parseInt(jersey, 10);
  const jerseyValid =
    /^\d{1,2}$/.test(jersey.trim()) &&
    jerseyNumber >= config.SHIRT_NUMBER_MIN &&
    jerseyNumber <= config.SHIRT_NUMBER_MAX;
  const nameValid = firstName.trim() !== '';
  const canSave = jerseyValid && nameValid && primary !== null;

  /**
   * §15.3 — jersey numbers are **not unique**, deliberately: a mid-season kit change would
   * otherwise need a temporary value, and a `ConstraintError` in the middle of an ordinary edit
   * costs more than an ambiguous referent. So this is a **non-blocking inline line**, never a
   * refusal — and under R-1 the referent is not even ambiguous on the wire, since it carries the
   * name beside the number.
   */
  const duplicate = jerseyValid
    ? (squad.find(
        (other) => other.id !== player?.id && other.jerseyNumber === jerseyNumber,
      ) ?? null)
    : null;

  const save = (): void => {
    if (!canSave || primary === null) return;
    /* `id` present = the handler's edit arm (keeps stored `createdAt`); absent = the facade
       mints one. A new player starts available — the switch below is how that changes. */
    onSave({
      ...(player !== null ? { id: player.id } : {}),
      jerseyNumber,
      firstName,
      primaryPosition: primary,
      secondaryPositions: secondaries,
      ratings,
      active: player?.active ?? true,
    });
  };

  const choosePrimary = (position: Position): void => {
    setPrimary(position);
    // A position cannot be its own secondary. Choosing a primary that was already a secondary
    // silently drops it from the second grid rather than leaving an illegal pair for the handler
    // to refuse — the grid renders the primary as *taken*, so the drop is visible, not silent.
    setSecondaries((current) => current.filter((each) => each !== position));
  };

  const toggleSecondary = (position: Position): void => {
    setSecondaries((current) =>
      current.includes(position)
        ? current.filter((each) => each !== position)
        : [...current, position],
    );
  };

  const setRating = (key: AttributeKey, rating: AttributeRating | undefined): void => {
    setRatings((current) => {
      const next = { ...current };
      if (rating === undefined) delete next[key];
      else next[key] = rating;
      return next;
    });
  };

  return (
    <SheetB
      titleId="myteam-player-title"
      title={player === null ? 'Add a player' : 'Edit player'}
      testId="myteam-player-sheet"
      onClose={onClose}
      actions={
        <>
          <button
            type="button"
            className="sheet-b__primary"
            data-testid="myteam-player-save"
            /* §3.7.36's "number, name and primary position required", stated by the control;
               `ERR_ROSTER_IDENTITY_REQUIRED` / `ERR_SHIRT_NUMBER_RANGE` in `commands/roster.ts`
               are what make it true for a caller that never met this button. */
            disabled={!canSave || busy}
            onClick={save}
          >
            Save
          </button>
          <button
            type="button"
            className="sheet-b__secondary"
            data-testid="myteam-player-cancel"
            onClick={onClose}
          >
            Cancel
          </button>
        </>
      }
    >
      {/* ── 1. Number and first name, on one row ──────────────────────────── */}
      <div className="myteam-identity">
        <div className="myteam-identity__number">
          <label className="sheet-b__label" htmlFor="myteam-player-jersey">
            Number
          </label>
          <input
            id="myteam-player-jersey"
            className="sheet-b__field"
            data-testid="myteam-player-jersey"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            maxLength={2}
            placeholder="7"
            value={jersey}
            onChange={(event) => {
              setJersey(event.target.value.replace(/\D/g, '').slice(0, 2));
            }}
          />
        </div>
        <div className="myteam-identity__name">
          <label className="sheet-b__label" htmlFor="myteam-player-firstname">
            First name
          </label>
          <input
            id="myteam-player-firstname"
            className="sheet-b__field"
            data-testid="myteam-player-firstname"
            type="text"
            autoComplete="off"
            placeholder="Maya"
            value={firstName}
            onChange={(event) => {
              setFirstName(event.target.value);
            }}
          />
        </div>
      </div>
      {duplicate !== null && (
        <p className="myteam-inline" data-testid="myteam-player-duplicate" role="status">
          #{jerseyNumber} is also on {duplicate.firstName}.
        </p>
      )}
      <p className="sheet-b__hint" data-testid="myteam-player-hint">
        Use their first name — this is your own squad, not the opposition.
      </p>

      {/* ── 2. Normally plays — single select ─────────────────────────────── */}
      <h3 className="myteam-section">Normally plays</h3>
      <PositionGrid
        testId="myteam-primary"
        ariaLabel="Normally plays"
        selected={primary === null ? [] : [primary]}
        disabled={busy}
        onToggle={choosePrimary}
      />

      {/* ── 3. Can also play — multi select, and the permission sentence ──── */}
      <h3 className="myteam-section">Can also play</h3>
      <p className="sheet-b__hint" data-testid="myteam-secondary-rule">
        Only positions you’d actually play them in a game that’s in doubt. This is permission for
        Coach Claude to move them.
      </p>
      <PositionGrid
        testId="myteam-secondary"
        ariaLabel="Can also play"
        selected={secondaries}
        taken={primary === null ? [] : [primary]}
        disabled={busy}
        onToggle={toggleSecondary}
      />
      <p
        className="myteam-permission"
        data-testid="myteam-permission"
        data-kind={secondaries.length === 0 ? 'fixed' : 'movable'}
      >
        {permissionCopy(secondaries)}
      </p>

      {/* ── 4. Ratings — four collapsible groups ──────────────────────────── */}
      <h3 className="myteam-section">Ratings</h3>
      <p className="sheet-b__hint" data-testid="myteam-ratings-preamble">
        Rate only what matters for this player. Blank means not assessed — Coach Claude never reads
        it as average.
      </p>
      {GROUP_ORDER.map((group) => {
        const rows = ATTRIBUTES.filter((attribute) => attribute.group === group);
        const rated = rows.filter((attribute) => ratings[attribute.key] !== undefined).length;
        const expanded = open.has(group);
        return (
          <section className="myteam-group" key={group} data-testid={`myteam-group-${group}`}>
            <button
              type="button"
              className="myteam-group__header"
              data-testid={`myteam-group-${group}-header`}
              aria-expanded={expanded}
              onClick={() => {
                setOpen((current) => {
                  const next = new Set(current);
                  if (next.has(group)) next.delete(group);
                  else next.add(group);
                  return next;
                });
              }}
            >
              <span className="myteam-group__title">{GROUP_TITLE[group]}</span>
              <span className="myteam-group__count">
                {rated === 0 ? 'none rated' : `${String(rated)} rated`}
              </span>
            </button>
            {/* `grid-template-rows: 0fr → 1fr` in the stylesheet: `interpolate-size` /
                `calc-size()` never shipped in any Safari, so this is the animatable form. */}
            <div className="myteam-group__body" data-open={expanded ? 'true' : 'false'}>
              <div className="myteam-group__inner">
                {rows.map((attribute) => (
                  <RatingControl
                    key={attribute.key}
                    attributeKey={attribute.key}
                    label={attribute.label}
                    value={ratings[attribute.key]}
                    disabled={busy}
                    onChange={(rating) => {
                      setRating(attribute.key, rating);
                    }}
                  />
                ))}
              </div>
            </div>
          </section>
        );
      })}

      {problem !== null && (
        <p className="sheet-b__problem" data-testid="myteam-player-problem" role="status">
          {problem}
        </p>
      )}

      {/* ── 5 and 6. Availability, then the armed remove ───────────────────── */}
      {player !== null && (
        <div className="sheet-b__danger">
          {!armedRemove ? (
            <button
              type="button"
              className="sheet-b__quiet"
              data-testid="myteam-player-remove"
              disabled={busy}
              onClick={() => {
                setArmedRemove(true);
              }}
            >
              Remove from roster…
            </button>
          ) : (
            <>
              <p className="sheet-b__hint">
                Remove {ourPlayerLabel(player)}? Their ratings go with them.
              </p>
              <button
                type="button"
                className="sheet-b__destructive"
                data-testid="myteam-player-remove-confirm"
                disabled={busy}
                onClick={() => {
                  onDelete(player.id);
                }}
              >
                Remove for good
              </button>
              <button
                type="button"
                className="sheet-b__quiet"
                data-testid="myteam-player-remove-keep"
                onClick={() => {
                  setArmedRemove(false);
                }}
              >
                Keep
              </button>
            </>
          )}
        </div>
      )}
    </SheetB>
  );
}

/**
 * §17.2's live permission copy — **the same two arms the renderer emits**, in the user's tense.
 *
 * A two-arm decision with no third arm, exactly as §18.2's position sentence is, because the
 * screen and the payload must agree about what an empty list means: silence there would read to
 * the model as *no information*, and it would cheerfully suggest the move.
 */
function permissionCopy(secondaries: readonly Position[]): string {
  if (secondaries.length === 0) return 'Coach Claude will never suggest moving this player.';
  const list =
    secondaries.length === 1
      ? secondaries[0]
      : `${secondaries.slice(0, -1).join(', ')} or ${secondaries[secondaries.length - 1]}`;
  return `Coach Claude may move this player to ${String(list)}, and nowhere else.`;
}

/** §17.2 — the first group when adding; every group that already has a rating when editing. */
function initialOpenGroups(player: OurPlayerRow | null): ReadonlySet<AttributeGroup> {
  if (player === null) return new Set<AttributeGroup>(['onTheBall']);
  const rated = new Set<AttributeGroup>();
  for (const attribute of ATTRIBUTES) {
    if (player.ratings[attribute.key] !== undefined) rated.add(attribute.group);
  }
  return rated.size === 0 ? new Set<AttributeGroup>(['onTheBall']) : rated;
}
